/**
 * Business-date helpers for Import Review. Dates are plain 'YYYY-MM-DD'
 * strings and all math is done in UTC so a user's timezone never shifts a day.
 */

/** SQL Server datetime / ISO / date → 'YYYY-MM-DD' (null when unparseable). */
export function toDateOnly(value) {
  if (!value) return null;
  const s = String(value).trim();
  const head = s.split(/[ T]/)[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : null;
}

export function addDays(isoDate, days) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole days from a to b (b - a). */
export function daysBetween(a, b) {
  return Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
}

/** Distinct row dates, sorted. */
export function distinctDates(...rowLists) {
  const set = new Set();
  rowLists.flat().forEach((r) => {
    const d = toDateOnly(r && r.effectiveDate);
    if (d) set.add(d);
  });
  return [...set].sort();
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-09-24' → 'Thu 24 Sep 2026'. */
export function longDay(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(`${isoDate}T00:00:00Z`);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** '2026-09-24' → '24 Sep'. */
export function shortDay(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(`${isoDate}T00:00:00Z`);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** One date → 'Thu 24 Sep 2026'; several → '24 Sep – 30 Sep (7 dates)'. */
export function describeDates(dates) {
  if (!dates.length) return '—';
  if (dates.length === 1) return longDay(dates[0]);
  return `${shortDay(dates[0])} – ${shortDay(dates[dates.length - 1])} (${dates.length} dates)`;
}

/**
 * The request body for POST /imports/:id/businessDate from the sheet's form.
 * form: { mode: 'set'|'shift', date, shift, updateFileRecord, refreshDashboard, note }
 * Returns { body } or { error }.
 */
export function buildMoveRequest(form, currentDates) {
  const note = (form.note || '').trim();
  const common = {
    updateFileRecord: form.updateFileRecord !== false,
    refreshDashboard: form.refreshDashboard !== false,
    ...(note ? { note } : {}),
  };
  if (form.mode === 'set') {
    if (!toDateOnly(form.date)) return { error: 'Pick a date' };
    if (currentDates.length === 1 && currentDates[0] === form.date) return { error: 'That is already the business date' };
    return { body: { effectiveDate: form.date, ...common } };
  }
  const n = Number(form.shift);
  if (!Number.isInteger(n) || n === 0) return { error: 'Enter a whole number of days, not 0' };
  if (Math.abs(n) > 366) return { error: 'Shift by at most 366 days' };
  return { body: { shiftDays: n, ...common } };
}

/** Where the dates land, for the preview. */
export function previewTargets(form, currentDates) {
  if (form.mode === 'set') {
    const unchanged = currentDates.length === 1 && currentDates[0] === form.date;
    return toDateOnly(form.date) && !unchanged ? [form.date] : [];
  }
  const n = Number(form.shift);
  if (!Number.isInteger(n) || n === 0) return [];
  return currentDates.map((d) => addDays(d, n));
}
